import L from 'leaflet';
import { mesh } from 'topojson-client';
import { AREA_TOGGLE_FADE_MS, areaToggleFor, easeAreaFade, whenAreaFadeReady } from './areaToggleFade.js';

// Grey areas with the lines off (Färger: Ingen), both views. Drawn see-through one by one on the area canvas,
// neighbouring grey areas leave light hairlines between them: antialiasing along shared edges, and slivers
// where the source outlines don't quite meet, which a border would otherwise cover. Here they're drawn solid on
// a canvas of their own, each with an edge in the same grey that closes those gaps, and the whole pane is made
// see-through at once, so overlaps never darken. The area's own path is then left unfilled.
//
// The pane sits just under the areas (z 398): above the base map and the hover map (250), below the area
// canvas, its outlines and the listing markers. A hovered grey area hides its cover and lightens itself as
// before.
//
// Where a grey area meets an active one (not grey, and inside the search when there is one) a black line runs
// along the shared edge (z 399), so the grey reads as a shape of its own; edges between grey areas, or towards
// the dimmed areas outside a search, get none. The edges come from the topology (topojson's mesh with a filter
// on the two areas each edge separates), recomputed shortly after the grey areas or the scope change.

const PANE = 'greyAreaCoverPane';
const BORDER_PANE = 'greyAreaBorderPane';
const GREY = '#4c525e';
const OPACITY = 0.6;
// Wide enough to close the slivers, and invisible: inside the pane it's the same solid grey.
const EDGE_WEIGHT = 2;
const BORDER_STYLE = { color: '#11161a', opacity: 0.9, weight: 1.5, lineJoin: 'round', lineCap: 'round' };
const areaCode = (geometry) => String(geometry?.properties?.regsokod || '').trim();

// The areas left out by the filters (selected, but outside them) get a second, lighter cover of their own,
// without borders (FILTERED_COVER).
export const FILTERED_COVER = { pane: 'filteredAreaCoverPane', zIndex: 397, fill: '#a3adab', opacity: 0.7, borders: false };

export const createGreyAreaCover = (map, {
    pane: paneName = PANE,
    zIndex = 398,
    fill = GREY,
    opacity: paneOpacity = OPACITY,
    borders = true,
} = {}) => {
    if (!map.getPane(paneName)) {
        const pane = map.createPane(paneName);
        pane.style.zIndex = String(zIndex);
        pane.style.opacity = String(paneOpacity);
        pane.style.pointerEvents = 'none';
    }
    if (borders && !map.getPane(BORDER_PANE)) {
        const pane = map.createPane(BORDER_PANE);
        pane.style.zIndex = '399';
        pane.style.pointerEvents = 'none';
    }
    const renderer = L.canvas({ pane: paneName, padding: 0.5 });
    const borderRenderer = borders ? L.canvas({ pane: BORDER_PANE, padding: 0.5 }) : null;
    // area layer -> { polygon, latLngs, rafId, showing }
    const covers = new Map();

    // Borders: the grey areas' codes (hovered ones included), the topology and which areas the search covers.
    const greyCodes = new Set();
    let topology = null;
    let inScope = () => true;
    let borderLayer = null;
    let borderTimer = null;
    const drawBorders = () => {
        borderTimer = null;
        if (!borders) return;
        const object = topology?.objects?.[Object.keys(topology.objects || {})[0]];
        if (!object || !greyCodes.size) {
            borderLayer?.remove();
            borderLayer = null;
            return;
        }
        const active = (code) => code && !greyCodes.has(code) && inScope(code);
        const lines = mesh(topology, object, (a, b) => {
            if (a === b) return false;
            const codeA = areaCode(a);
            const codeB = areaCode(b);
            return (greyCodes.has(codeA) && active(codeB)) || (greyCodes.has(codeB) && active(codeA));
        });
        const latLngs = lines.coordinates.map((line) => line.map(([lng, lat]) => [lat, lng]));
        if (!latLngs.length) {
            borderLayer?.remove();
            borderLayer = null;
            return;
        }
        if (borderLayer) borderLayer.setLatLngs(latLngs);
        else borderLayer = L.polyline(latLngs, { ...BORDER_STYLE, renderer: borderRenderer, interactive: false }).addTo(map);
    };
    const scheduleBorders = () => {
        if (borderTimer) return;
        borderTimer = setTimeout(drawBorders, 30);
    };
    const setGrey = (regsoCode, grey) => {
        const code = String(regsoCode || '').trim();
        if (!code || greyCodes.has(code) === grey) return;
        if (grey) greyCodes.add(code);
        else greyCodes.delete(code);
        scheduleBorders();
    };

    const fadeTo = (cover, target, onDone) => {
        if (cover.rafId) cancelAnimationFrame(cover.rafId);
        const start = cover.polygon.options.fillOpacity;
        const startedAt = performance.now();
        const step = (now) => {
            const t = Math.min(1, (now - startedAt) / AREA_TOGGLE_FADE_MS);
            const opacity = start + ((target - start) * easeAreaFade(t));
            cover.polygon.setStyle({ fillOpacity: opacity, opacity });
            if (t < 1) cover.rafId = requestAnimationFrame(step);
            else {
                cover.rafId = null;
                onDone?.();
            }
        };
        cover.rafId = requestAnimationFrame(step);
    };

    // regsoCode: an area just clicked on or off fades (areaToggleFade.js); anything else switches at once.
    const show = (layer, regsoCode) => {
        let cover = covers.get(layer);
        if (cover?.showing) return;
        const toggled = Boolean(areaToggleFor(regsoCode));
        if (!cover) {
            const latLngs = layer.getLatLngs();
            cover = {
                polygon: L.polygon(latLngs, {
                    renderer,
                    interactive: false,
                    fillColor: fill,
                    fillOpacity: toggled ? 0 : 1,
                    color: fill,
                    opacity: toggled ? 0 : 1,
                    weight: EDGE_WEIGHT,
                    lineJoin: 'round',
                }).addTo(map),
                latLngs,
                rafId: null,
                showing: true,
            };
            covers.set(layer, cover);
        }
        cover.showing = true;
        if (toggled) {
            const fading = cover;
            whenAreaFadeReady(regsoCode, () => {
                if (!fading.showing) return;
                setGrey(regsoCode, true);
                fadeTo(fading, 1);
            });
        } else {
            setGrey(regsoCode, true);
            if (cover.rafId) cancelAnimationFrame(cover.rafId);
            cover.rafId = null;
            cover.polygon.setStyle({ fillOpacity: 1, opacity: 1 });
        }
    };

    // keepGrey: still a grey area, only hovered (it keeps its border).
    const hide = (layer, regsoCode, { keepGrey = false } = {}) => {
        if (!keepGrey) setGrey(regsoCode ?? layer.feature?.properties?.regsokod, false);
        const cover = covers.get(layer);
        if (!cover || !cover.showing) return;
        cover.showing = false;
        const remove = () => {
            if (cover.showing) return;
            if (cover.rafId) cancelAnimationFrame(cover.rafId);
            cover.polygon.remove();
            covers.delete(layer);
        };
        if (areaToggleFor(regsoCode)) fadeTo(cover, 0, remove);
        else remove();
    };

    // The areas switch outline level with the zoom (areaGeometry.js): follow them.
    const syncOutlines = () => {
        covers.forEach((cover, layer) => {
            const latLngs = layer.getLatLngs();
            if (latLngs === cover.latLngs) return;
            cover.latLngs = latLngs;
            cover.polygon.setLatLngs(latLngs);
        });
    };
    let syncTimer = null;
    const handleZoomEnd = () => {
        clearTimeout(syncTimer);
        syncTimer = setTimeout(syncOutlines, 0);
    };
    map.on('zoomend', handleZoomEnd);

    // The topology the borders are cut from, and which areas count as active when not grey (inside the search).
    const setScope = (nextTopology, nextInScope) => {
        topology = nextTopology || null;
        inScope = nextInScope || (() => true);
        scheduleBorders();
    };

    const destroy = () => {
        clearTimeout(syncTimer);
        clearTimeout(borderTimer);
        borderLayer?.remove();
        greyCodes.clear();
        map.off('zoomend', handleZoomEnd);
        covers.forEach((cover) => {
            if (cover.rafId) cancelAnimationFrame(cover.rafId);
            cover.polygon.remove();
        });
        covers.clear();
    };

    return { show, hide, setScope, destroy };
};
